import {
  getAllBookedSpotNames,
  getMemberBookedSpotName,
  getWireBookedSpotNames,
  normalizeBookedSpots,
  mergeSnakeCamelBookedSpots,
  type BookedSpot
} from './bookedSpotNames';
import {
  getMemberBookedSpotNameFromWire,
  normalizeWireBookedSpotNames
} from './bookedSpotNamesWire';
import { readRawBookedSpotNamesFromRecord } from './bookedSpotNamesField';
import { readGuestBookingsFromRecord } from './guestBookingsField';
import {
  hasExplicitBookedSpotsArray,
  payloadHasExplicitStructuredSpots
} from './explicitStructuredSpots';

type BookingSpotBookingsSharedFields = {
  bookedSpots?: BookedSpot[];
  bookedSpotNames?: string[];
  guestBookings?: number;
};

export type BookingSpotBookingsUnresolved = BookingSpotBookingsSharedFields & {
  resolved: false;
};

export type BookingSpotBookingsResolved = BookingSpotBookingsSharedFields & {
  resolved: true;
  spotName: string;
};

export type BookingSpotBookingsResult = BookingSpotBookingsUnresolved | BookingSpotBookingsResolved;

export type BookingSpotBookingsNestedPayload = {
  /** Absent = field not in payload; `[]` = explicit empty assignment. */
  booked_spot_names?: string[] | string;
  bookedSpotNames?: string[] | string;
  booked_spots?: BookedSpot[];
  guest_bookings?: number;
  guestBookings?: number;
};

export type BookingSpotBookingsApiPayload = {
  booked_spot_names?: string[] | string;
  bookedSpotNames?: string[] | string;
  booked_spots?: BookedSpot[];
  guest_bookings?: number;
  guestBookings?: number;
  Booking?: BookingSpotBookingsNestedPayload;
};

export type HydratedBookingSpotFields = {
  booked_spots: BookedSpot[];
  booked_spot_names: string[];
  bookedSpotNames: string[];
};

export function getMemberSpotNameFromBookingResult(
  result: BookingSpotBookingsResult
): string | undefined {
  return result.resolved ? result.spotName : undefined;
}

/** Map legacy wire `booked_spot_names` to structured spots, preserving index → guest (incl. blank member slot). */
export function mapLegacyWireBookedSpotNamesToBookedSpots(
  bookedSpotNameOrNames: string[] | string | undefined
): BookedSpot[] {
  return normalizeWireBookedSpotNames(bookedSpotNameOrNames).map((spot_name, guest) => ({
    spot_name,
    guest
  }));
}

function readRawBookedSpotNamesField(
  booking: BookingSpotBookingsApiPayload | null | undefined
): string[] | string | undefined {
  return readRawBookedSpotNamesFromRecord(booking as Record<string, unknown> | null | undefined);
}

function readBookedSpotsPayload(source: Record<string, unknown>): BookedSpot[] {
  return mergeSnakeCamelBookedSpots(source['booked_spots'], source['bookedSpots']);
}

function readLegacyBookedSpotsFromPayload(source: Record<string, unknown>): BookedSpot[] {
  return mapLegacyWireBookedSpotNamesToBookedSpots(
    (source['booked_spot_names'] ?? source['bookedSpotNames']) as string[] | string
  );
}

function nestedBookingHasSpotData(nested: Record<string, unknown>): boolean {
  return (
    nested['booked_spot_names'] != null ||
    nested['bookedSpotNames'] != null ||
    hasExplicitBookedSpotsArray(nested)
  );
}

function readBookedSpotsRecord(source: unknown): Record<string, unknown> | null {
  if (!source || typeof source !== 'object') {
    return null;
  }
  const record = source as BookingSpotBookingsApiPayload;
  if (record.Booking && typeof record.Booking === 'object') {
    const nested = record.Booking as Record<string, unknown>;
    if (nestedBookingHasSpotData(nested)) {
      return nested;
    }
  }
  return record as Record<string, unknown>;
}

/** Resolve booked spots from API payloads, falling back to legacy name arrays only when `booked_spots` is absent. */
export function resolveBookedSpotsFromRecord(source: unknown): BookedSpot[] {
  const payload = readBookedSpotsRecord(source);
  if (!payload) {
    return [];
  }

  const bookedSpots = readBookedSpotsPayload(payload);
  if (bookedSpots.length > 0) {
    return bookedSpots;
  }

  if (hasExplicitBookedSpotsArray(payload)) {
    return [];
  }

  return readLegacyBookedSpotsFromPayload(payload);
}

function readBookedSpotNamesFromBookingPayload(
  booking: BookingSpotBookingsApiPayload | null | undefined
): string[] {
  const rawBookedSpotNames = readRawBookedSpotNamesField(booking);
  if (rawBookedSpotNames == null) {
    return [];
  }
  return normalizeWireBookedSpotNames(rawBookedSpotNames);
}

/** Parses `booked_spot_names` from hydrate / spot-bookings API payload shapes. Non-objects yield `[]`. */
export function readBookedSpotNamesFromWirePayload(payloadData: unknown): string[] {
  if (!payloadData || typeof payloadData !== 'object') {
    return [];
  }
  return readBookedSpotNamesFromBookingPayload(payloadData as BookingSpotBookingsApiPayload);
}

export function parseBookingSpotBookingsResponse(
  booking: BookingSpotBookingsApiPayload | null | undefined | Record<string, unknown>
): BookingSpotBookingsResult {
  const bookedSpots = resolveBookedSpotsFromRecord(booking);
  const typedBooking = booking as BookingSpotBookingsApiPayload | null | undefined;
  const rawBookedSpotNames = readRawBookedSpotNamesField(typedBooking);
  const bookedSpotNames =
    rawBookedSpotNames != null ? normalizeWireBookedSpotNames(rawBookedSpotNames) : undefined;
  const guestBookings = typedBooking
    ? readGuestBookingsFromRecord(typedBooking as Record<string, unknown>)
    : undefined;

  const hasStructuredField = bookedSpots.length > 0 || payloadHasExplicitStructuredSpots(booking);

  const spotNameFromStructured = getMemberBookedSpotName(bookedSpots);
  // An explicit empty booked_spots is authoritative — don't resurrect a member name from legacy wire names.
  const spotNameFromWire = hasStructuredField
    ? ''
    : getMemberBookedSpotNameFromWire(bookedSpotNames ?? []);
  const spotName = spotNameFromStructured || spotNameFromWire || undefined;

  const optionalFields: BookingSpotBookingsSharedFields = {
    ...(hasStructuredField ? { bookedSpots } : {}),
    ...(bookedSpotNames != null ? { bookedSpotNames } : {}),
    ...(guestBookings != null ? { guestBookings } : {})
  };

  if (!spotName) {
    return {
      resolved: false,
      ...optionalFields
    };
  }

  return {
    resolved: true,
    spotName,
    bookedSpots: hasStructuredField ? bookedSpots : (optionalFields.bookedSpots ?? []),
    ...(bookedSpotNames != null ? { bookedSpotNames } : {}),
    ...(guestBookings != null ? { guestBookings } : {})
  };
}

/** Keep legacy name fields in sync with resolved `booked_spots` after spot-bookings hydration. */
export function buildHydratedBookingSpotFields(booking: unknown): HydratedBookingSpotFields {
  const parsed = parseBookingSpotBookingsResponse(
    booking as BookingSpotBookingsApiPayload | Record<string, unknown> | null | undefined
  );

  if (payloadHasExplicitStructuredSpots(booking)) {
    const booked_spots = parsed.bookedSpots ?? [];
    const booked_spot_names = getWireBookedSpotNames(booked_spots);
    return {
      booked_spots,
      booked_spot_names,
      bookedSpotNames: booked_spot_names
    };
  }

  if (parsed.bookedSpotNames != null) {
    const booked_spot_names = parsed.bookedSpotNames;
    const booked_spots = mapLegacyWireBookedSpotNamesToBookedSpots(booked_spot_names);
    return {
      booked_spots,
      booked_spot_names,
      bookedSpotNames: booked_spot_names
    };
  }

  const bookedSpots = normalizeBookedSpots(parsed.bookedSpots ?? []);
  const booked_spot_names = getAllBookedSpotNames(bookedSpots);

  return {
    booked_spots: bookedSpots,
    booked_spot_names,
    bookedSpotNames: booked_spot_names
  };
}
